import * as React from "react";
import { FileText, MessageSquare, ShieldAlert, FileTextIcon } from "lucide-react";
import { Toggle } from "../toggle";
import { FileItem, type Location as FileLocation } from "@/types/fse";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { FlattenedItem } from "@/modules/chat/chat-ui.types";
import LocationsModal from "@/views/drive/modals/locationsModal";
import { MarkdownRenderer } from "@/components/feature/markdown/markdownRenderer";
import { Tooltip } from "@/components/ui/tooltip";
import { getRejectionLabel } from "@/views/drive/components/rejectionUtils";
import clsx from "clsx";

interface Props {
  item: FlattenedItem & FileItem;
  parentLocations: FileLocation[];
  handleFileClick: (file: FileItem) => void;
  handleFileSelect: (file: FileItem, isSelected: boolean) => void;
  handleAskFile: (fileId: string) => void;
  selectedFiles: Set<string>;
}

export const FileItemComponent: React.FC<Props> = ({
  item,
  parentLocations,
  handleFileClick,
  handleFileSelect,
  handleAskFile,
  selectedFiles,
}) => {
  const [summaryOpen, setSummaryOpen] = React.useState(false);
  const paddingLeft = `${item.depth * 10}px`;
  const isRejected = !!item.rejection_reason;

  return (
    <>
      <div style={{ paddingLeft }}>
        <div
          className={clsx(
            "rounded-lg border border-gray-200 bg-white p-3 shadow-sm transition-all duration-200 hover:border-gray-300 hover:shadow-md",
            { "border-red-200 bg-red-50/30 opacity-60": isRejected },
          )}
        >
          <div className="flex items-center gap-3">
            <div
              className="flex min-w-0 flex-grow cursor-pointer items-center gap-2"
              onClick={() => handleFileClick(item)}
            >
              <FileTextIcon className="h-5 w-5 flex-shrink-0 text-gray-500" />
              <span className="truncate text-sm font-medium text-gray-800" title={item.name}>
                {item.name}
              </span>
            </div>
            <div className="flex-shrink-0">
              {isRejected ? (
                <Tooltip
                  text={`Rejected: ${getRejectionLabel(item.rejection_reason!)}`}
                  className="rounded bg-black text-xs text-white"
                  delayDuration={50}
                >
                  <div className="flex items-center gap-1 text-xs text-red-500">
                    <ShieldAlert className="h-3 w-3" />
                    <Toggle enabled={false} onChange={() => {}} size="xs" disabled />
                  </div>
                </Tooltip>
              ) : (
                <Toggle
                  enabled={selectedFiles.has(item.id)}
                  onChange={enabled => handleFileSelect(item, enabled)}
                  size="xs"
                />
              )}
            </div>
          </div>
          <div className="mt-2 flex items-center gap-2">
            <Button
              variant="light-beige-secondary"
              size="xs"
              onClick={() => setSummaryOpen(true)}
              disabled={isRejected || !item.small_summary}
              className="gap-1.5 px-2 py-1 text-xs"
            >
              <FileText size={12} />
              Summary
            </Button>
            <Button
              variant="light-beige-secondary"
              size="xs"
              onClick={() => handleAskFile(item.id)}
              disabled={isRejected}
              className="gap-1.5 px-2 py-1 text-xs"
            >
              <MessageSquare size={12} />
              Q&A
            </Button>
            <LocationsModal itemName={item.name} locations={parentLocations} />
          </div>
        </div>
      </div>

      <Dialog open={summaryOpen} onOpenChange={setSummaryOpen}>
        <DialogContent className="max-h-[80vh] max-w-2xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileTextIcon className="h-5 w-5 text-gray-500" />
              {item.name}
            </DialogTitle>
          </DialogHeader>
          <div className="prose prose-sm max-w-none">
            {item.small_summary ? (
              <MarkdownRenderer content={item.small_summary} evidences={[]} processCitations={true} />
            ) : (
              <p className="text-gray-500">No summary available for this file.</p>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
